import React, {useState, useEffect, useRef} from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import { useBranding } from '../hooks/useBranding';
import BrandedHeader from '../components/BrandedHeader';
import CustomerCaptureForm from '../components/CustomerCaptureForm';
import {PRIMARY_COLOR} from "../contants/Colors";

const EMOJIS = [
  { value: 1, emoji: '😠', label: 'Angry',   color: '#ef4444' },
  { value: 2, emoji: '😢', label: 'Sad',     color: '#f97316' },
  { value: 3, emoji: '😐', label: 'Neutral', color: '#94a3b8' },
  { value: 4, emoji: '😊', label: 'Happy',   color: '#22c55e' },
  { value: 5, emoji: '🤩', label: 'Excited', color: '#8b5cf6' },
];

export default function ShareableFeedbackPage() {
  const { sessionCode } = useParams();
  const [link, setLink] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [customerInfo, setCustomerInfo] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const branding = useBranding(tenantId);

  useEffect(() => {
    api.get(`/links/public/${sessionCode}`)
      .then(r => {
        setLink(r.data.data);
        setTenantId(r.data.tenantId || r.data.data?.tenant?._id);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Link not found');
        setLoading(false);
      });
  }, [sessionCode]);

  const setAnswer = (questionId, questionText, emojiValue) => {
    setAnswers(prev => ({ ...prev, [questionId]: { questionId, questionText, emojiValue } }));
  };

  const handleSubmit = async () => {
    const questions = link?.feedbackForm?.questions || [];
    if (Object.keys(answers).length < questions.length) {
      setError('Please answer all questions before submitting.'); return;
    }
    setError(''); setSubmitting(true);
    try {
      await api.post(`/links/public/${sessionCode}/submit-emoji`, {
        answers: Object.values(answers),
        customerName: customerInfo.name || null,
        customerPhone: customerInfo.phone || null,
        customerEmail: customerInfo.email || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loading || branding.loading) return (
    <div className="page flex-center" style={{ minHeight: '100vh' }}>
      <div className="spinner" />
    </div>
  );

  if (error && !link) return (
    <div className="page flex-center" style={{ minHeight: '100vh' }}>
      <div className="card text-center" style={{ maxWidth: 400, padding: '48px 32px' }}>
        <div style={{ fontSize: 52, marginBottom: 12 }}>⚠️</div>
        <h3 style={{ marginBottom: 8 }}>{error}</h3>
        <p className="text-muted" style={{ fontSize: 14 }}>This feedback link may be invalid or already used.</p>
      </div>
    </div>
  );

  if (submitted) return (
      <div
          className="page flex-center"
          style={{minHeight: '100vh', background: branding.backgroundColor, display: 'flex', flexDirection: 'column'}}
      >
        <div className="card text-center" style={{maxWidth: 440, padding: '56px 40px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)'}}>
          <div style={{fontSize: 64, marginBottom: 16}}>🎉</div>
          <h2 style={{marginBottom: 10, color: branding.primaryColor}}>Thank You!</h2>
          <p className="text-muted" style={{fontSize: 15, lineHeight: 1.6}}>
            Your feedback means a lot to <strong>{branding.businessName}</strong>. We'll use it to keep improving!
          </p>
          {branding.logoUrl && (
              <img src={branding.logoUrl} alt="logo" style={{height: 40, marginTop: 24, objectFit: 'contain'}}/>
          )}
        </div>
        <div style={{
          fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
        }}>
          Powered by{' '}
          <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'}
               style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
        </div>
      </div>
  );

  const questions = link?.feedbackForm?.questions || [];

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setRecording(true);
      setAudioDuration(0);
      timerRef.current = setInterval(() => setAudioDuration(d => d + 1), 1000);
    } catch { setError('Microphone access denied.'); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const handleAudioSubmit = async () => {
    if (!audioBlob) { setError('Please record audio first.'); return; }
    setError(''); setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'feedback.webm');
      formData.append('sessionCode', sessionCode);
      formData.append('audioDuration', audioDuration);
      if (customerInfo.name)  formData.append('customerName',  customerInfo.name);
      if (customerInfo.phone) formData.append('customerPhone', customerInfo.phone);
      if (customerInfo.email) formData.append('customerEmail', customerInfo.email);
      await api.post(`/links/public/${sessionCode}/submit-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="page"
      style={{ minHeight: '100vh', background: branding.backgroundColor, color: branding.textColor }}
    >
      <div style={{maxWidth: 600, margin: '0 auto', padding: '40px 20px'}}>

        <BrandedHeader
            branding={branding}
            billId={link?.billId}
            subtitle={link?.feedbackForm?.title}
            recipientName={link?.recipientName}
        />

        {!link?.feedbackForm ? (
            /* No form attached — show audio recorder */
            <div className="card" style={{textAlign: 'center', padding: '40px 32px'}}>
              <div style={{fontSize: 48, marginBottom: 12}}>🎙️</div>
              <h3 style={{marginBottom: 8}}>Record Your Feedback</h3>
              <p className="text-muted mb-3" style={{fontSize: 14}}>
                Tell us about your experience in your own words.
              </p>

              {error && <div className="alert alert-error">{error}</div>}

              {recording && (
                  <div style={{marginBottom: 20}}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      background: '#fee2e2',
                      borderRadius: 20,
                      padding: '8px 18px',
                      color: '#b91c1c',
                      fontWeight: 600
                    }}>
                      <span style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#dc2626',
                        animation: 'pulse 1s infinite'
                      }}/>
                      Recording… {audioDuration}s
                    </div>
                  </div>
              )}

              {audioUrl && !recording && (
                  <div style={{marginBottom: 20}}>
                    <audio controls src={audioUrl} style={{width: '100%', borderRadius: 8}}/>
                    <p style={{fontSize: 12, color: 'var(--text-muted)', marginTop: 6}}>Duration: {audioDuration}s</p>
                  </div>
              )}

              <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16}}>
                {!recording && !audioBlob && (
                    <button
                        onClick={startRecording}
                        style={{
                          background: branding.primaryColor,
                          color: branding.buttonTextColor,
                          border: 'none',
                          borderRadius: 10,
                          padding: '12px 28px',
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                    >
                      🎙️ Start Recording
                    </button>
                )}
                {recording && (
                    <button
                        onClick={stopRecording}
                        style={{
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          padding: '12px 28px',
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                    >
                      ⏹ Stop
                    </button>
                )}
                {audioBlob && !recording && (
                    <>
                      <button
                          onClick={() => {
                            setAudioBlob(null);
                            setAudioUrl(null);
                            setAudioDuration(0);
                          }}
                          style={{
                            background: 'transparent',
                            color: branding.primaryColor,
                            border: `2px solid ${branding.primaryColor}`,
                            borderRadius: 10,
                            padding: '10px 20px',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                      >
                        🔄 Re-record
                      </button>
                      <button
                          onClick={handleAudioSubmit}
                          disabled={submitting}
                          style={{
                            background: branding.primaryColor,
                            color: branding.buttonTextColor,
                            border: 'none',
                            borderRadius: 10,
                            padding: '12px 28px',
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            opacity: submitting ? .7 : 1
                          }}
                      >
                        {submitting ? 'Submitting…' : '✅ Submit'}
                      </button>
                    </>
                )}
              </div>

              {audioBlob && !recording && (
                  <CustomerCaptureForm branding={branding} onChange={setCustomerInfo}/>
              )}
            </div>
        ) : (
            <div>
              {link.feedbackForm.description && (
                  <p style={{
                    textAlign: 'center',
                    marginBottom: 16,
                    fontSize: 15,
                    color: branding.textColor,
                    opacity: .75
                  }}>
                    {link.feedbackForm.description}
                  </p>
              )}

              {error && <div className="alert alert-error">{error}</div>}

              {questions.map((q, i) => {
                const selected = answers[q._id]?.emojiValue;
                return (
                    <div key={q._id} className="card mb-2">
                      <p style={{fontWeight: 600, marginBottom: 14, fontSize: 15, color: branding.textColor}}>
                        <span style={{color: branding.primaryColor, marginRight: 8}}>Q{i + 1}.</span>{q.text}
                      </p>
                      <div className="emoji-options" style={{justifyContent: 'center'}}>
                        {EMOJIS.map(e => (
                            <button
                                key={e.value}
                                type="button"
                                className={`emoji-btn ${selected === e.value ? 'selected' : ''}`}
                                style={selected === e.value
                                    ? {borderColor: branding.primaryColor, background: branding.primaryColor + '18'}
                                    : {}}
                                onClick={() => setAnswer(q._id, q.text, e.value)}
                            >
                              {e.emoji}
                              <span style={selected === e.value ? {color: branding.primaryColor} : {}}>{e.label}</span>
                            </button>
                        ))}
                      </div>
                    </div>
                );
              })}

              <CustomerCaptureForm branding={branding} onChange={setCustomerInfo}/>

              <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: 10,
                    border: 'none',
                    background: branding.primaryColor,
                    color: branding.buttonTextColor,
                    fontWeight: 700,
                    fontSize: 16,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? .7 : 1,
                    marginTop: 8,
                    transition: 'opacity .2s',
                  }}
              >
                {submitting ? 'Submitting…' : '✅ Submit Feedback'}
              </button>
            </div>
        )}
        <div style={{
          fontSize: 11, color: '#94a3b8', marginTop: 20, width: '100%', textAlign: 'center'
        }}>
          Powered by{' '}
          <span>
            <a href={'https://theperceptpulse.com'} target={'_blank'} style={{color: PRIMARY_COLOR, textDecoration: 'none'}}>
              Percept Pulse
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
